import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  ColumnSort,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  Updater,
} from '@tanstack/react-table'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import isNil from 'lodash.isnil'
import { autorun, computed, runInAction } from 'mobx'
import {
  ViewColumnTemplateFragment,
  UpdateViewMutation,
  ViewDataRowFragment,
  ViewType,
  GetOrCreateViewsMutation,
} from 'types/graphql'
import { useLocalStorage } from 'usehooks-ts'

import { useLocation } from '@redwoodjs/router'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import { GetViewsDataCountsForViewDashboard } from 'src/fetch/viewDashboards'
import { parseRecordsListViewUuidFromSearch } from 'src/fetch/views/recordsListViewUrl'
import {
  GetViewsDataCounts,
  TableViewMode,
  useUpdateViewMutation,
  useViewsDataQuery,
  useViewsQuery,
} from 'src/fetch/views/views'
import useDebounce from 'src/hooks/use-debounce'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { TableView } from 'src/models/TableViews/TableView'
import {
  TableViewManager,
  TableViewManagerSerialized,
} from 'src/models/TableViews/TableViewManager'
import { LocalStorageKey, RefetchFnType } from 'src/types'
import { deserializeMap, serializeMap } from 'src/utils'

const shouldPersistSearchQuery = (viewType: ViewType): boolean => {
  switch (viewType) {
    case 'RecordsForStaffInModule':
    case 'RecordTaskInspectionAttemptsForStaffInModule':
      return true
    default:
      return false
  }
}

const shouldApplyViewUuidFromUrl = (viewType: ViewType): boolean =>
  viewType === 'RecordsForStaffInModule'

export type UseTableViewManagerReturn = {
  columnTemplates: ViewColumnTemplateFragment[]
  generateDefaultViews: () => Promise<GetOrCreateViewsMutation>
  getRowId: (row: ViewDataRowFragment) => string
  hasViewsDataLoaded: boolean
  hoveredRow: Row<ViewDataRowFragment> | undefined
  isFetchingViewData: boolean
  isLoadingViewData: boolean
  isLoadingViews: boolean
  onPaginationChange: (pagination: PaginationState) => void
  onRowSelectionChange: (rowSelection: RowSelectionState) => void
  viewCountLoadingStates: Map<number, boolean>
  onSaveViewChanges: () => Promise<void>
  onSortingChange: OnChangeFn<ColumnSort[]>
  pagination: PaginationState
  refetchDatabaseCountForView: (view: TableView) => Promise<void>
  refetchViewData: RefetchFnType
  rows: ViewDataRowFragment[]
  rowSelection: RowSelectionState
  searchQuery: string
  debouncedSearchQuery: string
  selectedRows: ViewDataRowFragment[]
  setSearchQuery: Dispatch<SetStateAction<string>>
  sorting: ColumnSort[]
  tableViewManager: TableViewManager
  viewType: ViewType
}

export const useTableViewManager = (args: {
  entityLayerId?: number
  mode?: TableViewMode
  recordTypeId?: number
  viewDashboardId?: number
  viewType: ViewType
}): UseTableViewManagerReturn => {
  const { entityLayerId, mode = TableViewMode.Edit, recordTypeId, viewDashboardId, viewType } = args
  const organizationId = useMyOrgId()
  const { notification } = useNotification()
  const { search } = useLocation()
  const viewUuidFromUrl = useMemo(
    () =>
      shouldApplyViewUuidFromUrl(viewType) ? parseRecordsListViewUuidFromSearch(search) : null,
    [search, viewType]
  )
  const hasAppliedViewUuidFromUrlRef = useRef(false)
  const persistSearchQuery = shouldPersistSearchQuery(viewType)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery)

  const [tableViewManager] = useState(
    new TableViewManager({
      recordTypeId,
      viewDashboardId,
      viewType,
    })
  )

  const tableViewManagerKey = `${viewType}-${recordTypeId}-${viewDashboardId}-${entityLayerId ?? ''}`
  const [initializedForKey, setInitializedForKey] = useState<string | null>(null)
  const [tableViewManagerSerializedByKey, setTableViewManagerSerializedByKey] = useLocalStorage<
    Map<string, TableViewManagerSerialized>
  >(LocalStorageKey.TableViewManagers, new Map<string, TableViewManagerSerialized>(), {
    serializer: serializeMap,
    deserializer: deserializeMap,
  })

  const { hoveredRow, pagination, onPaginationChange, onRowSelectionChange, rowSelection } =
    useTableState<ViewDataRowFragment>({
      autoResetKey: `${tableViewManager.selectedView?.id}-${tableViewManager.selectedView?.filterManager.populatedFilters?.map((f) => JSON.stringify(f.protocol)).join('')}`,
    })

  const sorting: ColumnSort[] = computed(
    () =>
      tableViewManager.selectedView?.sortManager.sorts.map((sort) => ({
        id: sort.columnTemplateGeneratedId,
        desc: sort.mode === 'Desc',
      })) ?? []
  ).get()

  const onSortingChange = useCallback(
    (newSorting: Updater<ColumnSort[]>) => {
      runInAction(() => {
        if (!tableViewManager.selectedView) {
          return
        }
        if (!newSorting) {
          tableViewManager.selectedView.sortManager.sorts = []
        } else {
          const value = newSorting instanceof Function ? newSorting(sorting) : newSorting
          tableViewManager.selectedView.sortManager.sorts = value.map((sort) => ({
            columnTemplateGeneratedId: sort.id,
            mode: sort.desc ? 'Desc' : 'Asc',
          }))
        }
      })
    },
    [sorting, tableViewManager.selectedView]
  )

  const {
    columnTemplates,
    generateDefaultViews,
    views,
    viewsDataCounts,
    isLoading: isLoadingViews,
  } = useViewsQuery({
    entityLayerId,
    mode,
    recordTypeId,
    viewDashboardId,
    viewType,
  })
  const {
    viewData,
    isLoading: isLoadingViewData,
    isFetching: isFetchingViewData,
    refetch: refetchViewData,
    isSuccess: hasViewsDataLoaded,
  } = useViewsDataQuery({
    inputs: [
      {
        viewId: tableViewManager.selectedView?.id,
        viewInput: tableViewManager.getCreateViewInput(tableViewManager.selectedView?.protocol),
        searchQuery: debouncedSearchQuery,
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        organizationId,
      },
    ],
    mode,
    viewDashboardId,
  })
  const totalCount = viewData?.totalCount
  useEffect(() => {
    runInAction(() => {
      const view = tableViewManager.selectedView
      if (!view || !viewData) {
        return
      }
      view.totalCount = totalCount ?? 0
    })
  }, [tableViewManager.selectedView, totalCount, viewData])

  // Track which view counts are still loading
  const viewCountLoadingStates = useMemo(() => {
    const loadingMap = new Map<number, boolean>()
    viewsDataCounts.forEach((vc) => {
      loadingMap.set(vc.viewId, vc.isLoading)
    })
    return loadingMap
  }, [viewsDataCounts])

  // Initialize views with latest data
  useEffect(() => {
    runInAction(() => {
      if (!columnTemplates.length || !views.length) {
        return
      }
      if (initializedForKey !== tableViewManagerKey) {
        // In View mode, don't load database filters into FilterManager - they act as invisible
        // constraints on the backend. Users can add their own ephemeral filters on top.
        const hideBaseFilters = mode === TableViewMode.View
        tableViewManager.initializeViews({ columnTemplates, views, hideBaseFilters })
        setInitializedForKey(tableViewManagerKey)
      }
      // Update counts for views that have finished loading
      viewsDataCounts.forEach((vc) => {
        if (!vc.isLoading && !isNil(vc.totalCount)) {
          const view = tableViewManager.getViewById(vc.viewId)
          if (view) {
            view.databaseTotalCount = vc.totalCount
          }
        }
      })
    })
  }, [
    columnTemplates,
    initializedForKey,
    mode,
    tableViewManager,
    tableViewManagerKey,
    views,
    viewsDataCounts,
  ])

  const hasInitializedViews = !!tableViewManager.views.length
  const [hasLoadedFromCache, setHasLoadedFromCache] = useState(false)

  // Reset cache merge when switching entity layer / record type scope
  useEffect(() => {
    runInAction(() => {
      tableViewManager.reset()
    })
    setHasLoadedFromCache(false)
    setInitializedForKey(null)
    hasAppliedViewUuidFromUrlRef.current = false
  }, [tableViewManager, tableViewManagerKey])

  useEffect(() => {
    hasAppliedViewUuidFromUrlRef.current = false
  }, [viewUuidFromUrl])

  useEffect(() => {
    if (
      !viewUuidFromUrl ||
      !hasInitializedViews ||
      !hasLoadedFromCache ||
      hasAppliedViewUuidFromUrlRef.current
    ) {
      return
    }

    let selectedViewFromUrl = false
    runInAction(() => {
      selectedViewFromUrl = tableViewManager.setSelectedViewUuid(viewUuidFromUrl)
    })
    if (!selectedViewFromUrl) {
      notification.warning({
        message: 'Could not find the saved view from this link. Showing your last selected tab.',
      })
    }
    hasAppliedViewUuidFromUrlRef.current = true
  }, [hasInitializedViews, hasLoadedFromCache, notification, tableViewManager, viewUuidFromUrl])

  // Checks for cached views data and merges it into the latest views
  const mergeLocalStorageDataIntoViews = useCallback(() => {
    runInAction(() => {
      const mergedViewsById = new Map(views.map((v) => [v.id, v]))
      const serialized = tableViewManagerSerializedByKey.get(tableViewManagerKey)
      if (serialized) {
        for (const cachedView of serialized.views) {
          const savedView = mergedViewsById.get(cachedView.id)
          if (!savedView) {
            // Assume this view was deleted in another session
            continue
          }

          // Only use the cached data if it is newer than the saved data
          if (new Date(cachedView.updatedAt) >= new Date(savedView.updatedAt)) {
            const view = tableViewManager.getViewById(cachedView.id)
            if (view) {
              view.sortManager.setSorts(cachedView.sorts)
              // In View mode, don't restore cached filters - users start with fresh filter state
              // and base filters are applied invisibly on the backend
              if (mode !== TableViewMode.View) {
                const filtersById = new Map(
                  cachedView.filters.map((f) => [f.columnTemplateGeneratedId, f])
                )
                view.filterManager.filters.forEach((f) => {
                  const cachedFilter = filtersById.get(f.columnTemplateGeneratedId)
                  if (cachedFilter) {
                    f.setValues(cachedFilter)
                  } else {
                    f.clear()
                  }
                })
              }
            }
          }
        }

        // Set the previously selected view if it is still in the latest views
        if (serialized.selectedViewId && tableViewManager.getViewById(serialized.selectedViewId)) {
          tableViewManager.setSelectedViewId(serialized.selectedViewId)
        }

        if (persistSearchQuery && serialized.searchQuery) {
          setSearchQuery(serialized.searchQuery)
        }
      }
    })
  }, [
    mode,
    persistSearchQuery,
    setSearchQuery,
    tableViewManager,
    tableViewManagerKey,
    tableViewManagerSerializedByKey,
    views,
  ])

  useEffect(() => {
    if (!hasInitializedViews || hasLoadedFromCache) {
      // Bail out if views haven't loaded yet
      // Only call ONCE; avoids loading from cache if user deleted all views
      return
    }
    setHasLoadedFromCache(true)
    mergeLocalStorageDataIntoViews()
  }, [hasInitializedViews, hasLoadedFromCache, mergeLocalStorageDataIntoViews, views.length])

  // Update local storage whenever serialized view data changes (filters, sorts, selected view, search query, etc.)
  useEffect(() => {
    if (!hasInitializedViews) {
      // Avoid overwriting local storage until table view manager is populated
      return
    }
    // Don't save to localStorage in View mode - users can't save changes anyway,
    // and we don't want to overwrite Edit mode's saved state
    if (mode === TableViewMode.View) {
      return
    }
    const dispose = autorun(() => {
      const serialized = tableViewManager.serialized
      setTableViewManagerSerializedByKey((prev) => {
        const next = new Map(prev)
        next.set(tableViewManagerKey, {
          ...serialized,
          searchQuery: persistSearchQuery ? searchQuery : undefined,
        })
        return next
      })
    })
    return () => dispose()
  }, [
    persistSearchQuery,
    tableViewManager,
    tableViewManagerKey,
    setTableViewManagerSerializedByKey,
    hasInitializedViews,
    mode,
    searchQuery,
  ])

  const handleViewChangesSaved = useCallback(
    (data: UpdateViewMutation) => {
      runInAction(() => {
        tableViewManager.getViewById(data.updateView.id)?.save(new Date(data.updateView.updatedAt))
      })
    },
    [tableViewManager]
  )
  const { mutateAsync: updateView } = useUpdateViewMutation({
    onSuccess: handleViewChangesSaved,
  })

  const onSaveViewChanges = useCallback(async () => {
    await runInAction(async () => {
      if (!tableViewManager.selectedView) {
        return
      }
      await updateView({
        id: tableViewManager.selectedView.id,
        input: tableViewManager.getCreateViewInput(tableViewManager.selectedView.protocol),
      })
    })
  }, [tableViewManager, updateView])

  const apolloClient = useApolloClient()
  const refetchDatabaseCountForView = useCallback(
    async (view: TableView) => {
      const result = await runInAction(async () => {
        // In View mode, use the dashboard query which applies base filters on the backend
        if (mode === TableViewMode.View && viewDashboardId) {
          return apolloClient.query({
            query: GetViewsDataCountsForViewDashboard,
            variables: {
              dashboardId: viewDashboardId,
              inputs: [
                {
                  viewId: view.id,
                },
              ],
            },
          })
        }

        const viewInput = tableViewManager.getCreateViewInput(view.databaseProtocol)
        return apolloClient.query({
          query: GetViewsDataCounts,
          variables: {
            inputs: {
              viewInput,
              organizationId,
            },
          },
        })
      })

      runInAction(() => {
        view.databaseTotalCount =
          result.data?.getViewsDataForViewDashboard?.[0]?.totalCount ??
          result.data?.getViewsData?.[0]?.totalCount ??
          0
      })
    },
    [apolloClient, mode, organizationId, tableViewManager, viewDashboardId]
  )

  const getRowId = useCallback(
    // use fileOnRecordType here since the FilesInModule view does not remove duplicates
    (r: ViewDataRowFragment) => `${r.id}.${r.fileOnRecordType || ''}`,
    []
  )

  const rows = useMemo(() => viewData?.rows ?? [], [viewData?.rows])
  const selectedRows = useMemo(() => {
    return rows.filter((row) => !!rowSelection[getRowId(row)])
  }, [rows, rowSelection, getRowId])

  const handleRefetchViewData = useCallback(async () => {
    onRowSelectionChange({})
    await refetchViewData()
  }, [onRowSelectionChange, refetchViewData])

  return {
    columnTemplates,
    generateDefaultViews,
    getRowId,
    hasViewsDataLoaded,
    hoveredRow,
    isFetchingViewData,
    isLoadingViews,
    isLoadingViewData,
    onPaginationChange,
    onRowSelectionChange,
    onSaveViewChanges,
    onSortingChange,
    pagination,
    refetchDatabaseCountForView,
    refetchViewData: handleRefetchViewData,
    rows,
    rowSelection,
    searchQuery,
    debouncedSearchQuery,
    selectedRows,
    setSearchQuery,
    sorting,
    tableViewManager,
    viewCountLoadingStates,
    viewType,
  }
}
